import React from 'react';
import { cn } from '~/lib/utils';
import { BubbleFooter } from './BubbleFooter';
import { Caret } from './Caret';
import { MessageActions } from './MessageActions';
import { QueueMeta } from './QueueMeta';
import { UserBubble } from './UserBubble';
import type { UserBubbleProps } from './UserBubble';
import styles from './UserMessage.module.css';
import type { ChatTheme } from './theme';
import { chatThemeClass } from './theme';
import { useV15Theme } from '~/design-system/components/v1_5/theme-context';

/**
 * The clamp, as this composite takes it.
 *
 * `BubbleFooter` is fully controlled — it is a dumb row that renders the state it is handed. The
 * turn above it is where the state actually belongs, so `expanded` becomes optional here:
 * omit it and the composite holds the toggle itself (`Long - clamped` -> `Long - expanded` with no
 * wiring at all),
 * pass it and the composite defers, the usual controlled/uncontrolled contract.
 *
 * `onToggle` also gains the next value as an argument, which `BubbleFooter`'s own signature does
 * not have. A controlled caller needs it to set state; an uncontrolled one can ignore it.
 */
export interface UserMessageClamp {
  /** Rendered "{n} characters". The count is of the whole message, not of what is visible. */
  characterCount: number;
  /** Omit for uncontrolled. Present makes the composite defer to the caller. */
  expanded?: boolean;
  onToggle?: (expanded: boolean) => void;
}

/**
 * The queue's per-message controls.
 *
 * The status word is NOT here: it is derived from `bubble.editing`, so the caret and the word can
 * never disagree with each other (`Editing a queued one` is exactly the state where they must not).
 */
export interface UserMessageQueue {
  /** Drops this message from the queue. */
  onCancel?: () => void;
  /**
   * Accessible name for the X. `Three queued` stacks three identical ones, so this is worth
   * passing.
   */
  cancelLabel?: string;
}

/**
 * Where the published prop surface differs from INVENTORY §1.40, and why. All three deltas are
 * settled in DECISIONS.md item 9c; §1.40's literal shapes are named here so a caller written against
 * the inventory can see what moved instead of just failing to compile.
 *
 * - §1.40 publishes `footer?: BubbleFooterProps`. It is `UserMessageClamp` here: `expanded` is
 *   optional (the composite owns the toggle when the caller does not) and `onToggle` gains the next
 *   value. `BubbleFooter` keeps its own fully-controlled shape; this is the composite's.
 * - §1.40 publishes `queue?: QueueMetaProps`, which carries `status`. That field is **not** accepted
 *   here — the status word is derived from `bubble.editing`, so the caret and the word cannot
 *   disagree (`Editing a queued one` is exactly the state where they must not).
 * - §1.40 publishes `bubble?: Omit<UserBubbleProps, 'children'>`. `theme` and `className` are
 *   omitted too: the composite passes its own `theme` down to all six parts, so a `theme` inside
 *   `bubble` would be silently dropped rather than honoured.
 */
export interface UserMessageProps {
  /** The message body. In `editing` the caret is appended for you — do not pass one. */
  children: React.ReactNode;
  /**
   * What rides 8px ABOVE the bubble: a `MessageAttachmentRow`, a `MessageImageGrid` or a
   * `RegionReference`. The 8px is applied here rather than taken from the node, so all three land
   * at the same distance — see the stylesheet.
   */
  attachments?: React.ReactNode;
  /**
   * Everything about the bubble except its content. `clamped` is ignored while `footer` is
   * present: a footer means this turn owns the clamp, and two owners would let the label lie.
   */
  bubble?: Omit<UserBubbleProps, 'children' | 'theme' | 'className'>;
  /**
   * The clamp footer (`Long - clamped`, `Long - expanded`, `Very long`).
   *
   * **Not** mutually exclusive with `onCopy` / `onEdit`, though INVENTORY §1.40 calls it that and no
   * artboard draws both. The rule §1.40 is describing is a fact about the boards, not a constraint:
   * user-message.md's own progressive-disclosure model reads "4. Hover (**any length**) → two 15px
   * actions 4px below, 12px apart, bubble untouched", and a 438-character message is a length. A
   * clamped message you happen to be pointing at is therefore a reachable state, and suppressing
   * the actions there would take copy and edit away from exactly the messages long enough to want
   * them. The stories still draw the two separately, because that is what the design draws.
   */
  footer?: UserMessageClamp;
  /** Queue controls. Only ever rendered when `bubble.kind` is `queued` — see the component. */
  queue?: UserMessageQueue;
  /**
   * The 6px of block padding on the message row (INVENTORY §1.40, "Not a prop").
   *
   * It stays a prop anyway, because the design gives the same 6px to a different owner in each of
   * the three places this component lands and two owners paint 12 where the design draws 6. Default
   * **on**, which is the loose-in-the-column case the ten `User message` boards draw. Pass `false`
   * inside a `ConversationTurn` (its `.user` slot already carries the 6px, per INVENTORY §1.45) and
   * for consecutive rows in a 12px column (queue.md's atoms A and B carry none, and `Three queued`
   * says the column's `gap: 12px` is the whole separation). The stylesheet has the full note.
   */
  padded?: boolean;
  /**
   * Forces the hover reveal. Stories and visual tests only, on `IconSlot`'s precedent: the real
   * reveal is CSS and costs no render. `false` does NOT hide the row from a real pointer — this
   * is an override for the "no pointer at all" case, not a second source of truth.
   */
  hovered?: boolean;
  onCopy?: () => void;
  onEdit?: () => void;
  theme?: ChatTheme;
  className?: string;
}

/**
 * One complete user turn: the bubble, plus everything Paper hangs off it, in the rhythm it hangs
 * them — attachments 8px above, footer / queue meta / hover actions 4px below, the whole stack
 * right-aligned on a rail that is wider than the 320px bubble.
 *
 * It composes six audited parts and adds no chrome of its own. What it genuinely owns is the state
 * those parts cannot hold individually:
 *
 * - **the hover reveal** (`Hover`) — CSS, on the row, so a thread of fifty messages does not
 *   re-render on every pointer move;
 * - **the clamp toggle** (`Long - clamped` -> `Long - expanded`) — one boolean feeding both the
 *   bubble's clamp and the footer's label, because a "Show more" over an unclamped bubble is a lie;
 * - **the editing state** — one boolean feeding the bubble's ring, the caret, and the queue's
 *   status word, for the same reason;
 * - **the queued kind** — `kind: 'queued'` is what puts the meta line under the bubble. The design
 *   draws no queued message without it, so it is not a separate opt-in.
 *
 * Per DECISIONS.md item 5, editing is the `Editing` board's treatment for both sent and queued
 * bubbles — `Editing a queued one` draws neither the ring nor the caret its own caption asks for.
 */
export const UserMessage = React.forwardRef<HTMLDivElement, UserMessageProps>(function UserMessage(
  {
    children,
    attachments,
    bubble,
    footer,
    queue,
    padded = true,
    hovered = false,
    onCopy,
    onEdit,
    theme: themeProp,
    className,
  },
  ref
) {
  const theme = useV15Theme(themeProp);
  const kind = bubble?.kind ?? 'sent';
  const editing = bubble?.editing ?? false;

  // Uncontrolled by default, and the default is clamped: `Long - clamped` is what a long message
  // looks like when it arrives, and `Long - expanded` is what it looks like after you ask.
  const [selfExpanded, setSelfExpanded] = React.useState(false);
  const controlled = footer?.expanded !== undefined;
  const expanded = controlled ? Boolean(footer?.expanded) : selfExpanded;

  const onToggle = footer?.onToggle;
  const handleToggle = React.useCallback(() => {
    const next = !expanded;
    if (!controlled) {
      setSelfExpanded(next);
    }
    onToggle?.(next);
  }, [controlled, expanded, onToggle]);

  // A footer is the declaration that this message is long enough to clamp, so it wins over
  // `bubble.clamped` outright rather than being ANDed with it.
  const clamped = footer ? !expanded : bubble?.clamped ?? false;

  // The actions are the only edit affordance in eighty artboards, which is how `Editing a queued
  // one` (a queued message under the caret) is ever reached — so they are offered on queued bubbles
  // too, and withheld only while the message IS being edited, where copy and edit have nothing to do.
  // Withheld too when neither handler exists: the row reserves its own height whether or not it is
  // revealed, and reserving space for two dead buttons is worse than not drawing them.
  //
  // NOT withheld by `footer`, even though INVENTORY §1.40 says the two are mutually exclusive: the
  // ten user-message boards draw the clamp and the hover on separate artboards, but the column's
  // own disclosure model says hover applies at "any length", so a hovered clamped message is a
  // state the design has and simply never drew twice on one board. See the `footer` prop's TSDoc.
  const showActions = !editing && Boolean(onCopy || onEdit);

  return (
    <div
      ref={ref}
      // No role and no label. This is the message's layout row; the readable objects are the text
      // inside the bubble and the named controls under it, and wrapping them in a group would only
      // add a level for a screen reader to walk out of.
      className={cn(
        chatThemeClass(theme),
        styles.root,
        padded && styles.padded,
        hovered && styles.forced,
        className
      )}
    >
      <div className={styles.rail}>
        {attachments ? <div className={styles.attachments}>{attachments}</div> : null}

        {/* Paper's "Bubble stack": the box the footer measures its right edge against. Without it
            the footer's `align-self: stretch` would stretch to the 400px rail and throw the
            character count against the panel edge instead of the bubble's. */}
        <div className={styles.stack}>
          <UserBubble {...bubble} clamped={clamped} theme={theme}>
            {children}
            {/* Appended rather than passed in, so a caller cannot ship an editing bubble without a
                caret or a resting one with it. When the bubble becomes a real `<textarea>` this
                goes and the browser's own caret takes over — `Caret` exists because Paper had to
                draw one, not because we want to own text editing. */}
            {editing ? <Caret theme={theme} className={styles.caret} /> : null}
          </UserBubble>

          {footer ? (
            <BubbleFooter
              expanded={expanded}
              characterCount={footer.characterCount}
              onToggle={handleToggle}
              theme={theme}
            />
          ) : null}
        </div>

        {/* Driven by `kind`, not by the presence of `queue`. All four Queue artboards put this line
            under every queued bubble, so "queued with no status word" is not a state the design
            has — reaching it through a missing prop would just be a way to draw it wrong. */}
        {kind === 'queued' ? (
          <QueueMeta
            status={editing ? 'editing' : 'queued'}
            onCancel={queue?.onCancel}
            cancelLabel={queue?.cancelLabel}
            theme={theme}
          />
        ) : null}

        {showActions ? (
          <div className={styles.actions}>
            {/* `visible` is deliberately not passed, so the row stays at its default. Its hidden
                state is `visibility: hidden`, which takes the two buttons out of the tab order —
                correct for a row whose owner reveals it some other way, wrong here, where hover is
                the only way in and a keyboard user would never get one. The slot around it fades
                on opacity instead and reveals itself on `:focus-within`, so Tab lands on Copy and
                the pair appears. Same 130ms, same curve, same reserved space. */}
            <MessageActions side="user" onCopy={onCopy} onEdit={onEdit} theme={theme} />
          </div>
        ) : null}
      </div>
    </div>
  );
});
